import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { Invoice } from "../../../../pojo/Pricing";
import { S25IconComponent } from "../../../s25-icon/s25.icon.component";
import { S25DndSortableModule } from "../../../s25-dnd-sortable/s25.dnd.sortable.module";
import { S25Util } from "../../../../util/s25-util";
import { PricingService } from "../../../../services/pricing.service";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { S25FadingSuccessMsgComponent } from "../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25IconButtonComponent } from "../../../../standalone/s25.icon.button.component";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { Bind } from "../../../../decorators/bind.decorator";
import { invoiceDisplayName, PricingStore } from "../s25.event.pricing.store";

@Component({
    selector: "s25-pricing-sidebar",
    template: `
        <div class="action-bar">
            @if (sortChanged()) {
                <button
                    type="button"
                    class="c-textButton"
                    [class.hidden]="!sidebarOpen()"
                    [attr.inert]="sidebarOpen() ? null : ''"
                    (click)="saveSort()"
                >
                    @if (savingSort()) {
                        <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                    } @else {
                        <s25-ng-icon [type]="'save'" [ariaLabel]="false" aria-hidden="true"></s25-ng-icon>
                    }
                    Save Sort
                </button>
            }
            @if (successMsg()) {
                <s25-ng-fading-success-msg [(message)]="successMsg" aria-live="polite"></s25-ng-fading-success-msg>
            }
            <button
                type="button"
                class="menu-button"
                [attr.aria-expanded]="sidebarOpen()"
                aria-controls="pricing-invoice-list"
                (click)="pivotSidebar()"
            >
                <s25-ng-icon
                    [style.--icon--rotation-degrees]="sidebarOpen() ? '0deg' : '180deg'"
                    [type]="'doubleArrowLeft'"
                    [ariaLabel]="sidebarOpen() ? 'Collapse sidebar' : 'Expand sidebar'"
                ></s25-ng-icon>
            </button>
        </div>
        <nav
            id="pricing-invoice-list"
            aria-label="Invoice list"
            [class.hidden]="!sidebarOpen()"
            [attr.inert]="sidebarOpen() ? null : ''"
        >
            <ul
                class="invoice-list"
                s25-ng-dnd-sortable
                [items]="invoices()"
                [sortable]="canSort()"
                (orderChanged)="onOrderChanged($event)"
            >
                <!-- Carries no sortable-item directive, so the sort never sees it. -->
                <li class="summary" [class.active]="summaryActive()">
                    <span class="invoice-item" [class.sortable]="canSort()">
                        @if (canSort()) {
                            <span class="drag-handle-spacer" aria-hidden="true"></span>
                        }
                        <button
                            type="button"
                            class="invoice-name"
                            [attr.aria-current]="summaryActive() ? 'true' : null"
                            (click)="showSummary()"
                        >
                            Invoice Summary
                        </button>
                    </span>
                </li>
                @for (invoice of invoices(); track invoice.id) {
                    @let selected = !summaryActive() && invoice.id === selectedInvoice()?.id;
                    <li
                        s25-ng-dnd-sortable-item
                        [index]="$index"
                        [draggable]="canSort()"
                        [class.active]="selected"
                        (keydown.escape)="editNameIndex() === $index ? editNameIndex.set(null) : null"
                    >
                        <span class="invoice-item" [class.sortable]="canSort()">
                            @if (canSort()) {
                                <s25-ng-icon
                                    class="drag-handle"
                                    [type]="'dragHandle'"
                                    [ariaLabel]="false"
                                    aria-hidden="true"
                                ></s25-ng-icon>
                            }
                            <button
                                type="button"
                                class="invoice-name"
                                [attr.aria-current]="selected ? 'true' : null"
                                (click)="selectInvoice(invoice)"
                            >
                                {{ displayName(invoice) }}
                            </button>
                            @if (canEditPricing()) {
                                <s25-ng-icon-button
                                    [type]="'edit'"
                                    [size]="20"
                                    [ariaLabel]="'Edit invoice name'"
                                    (clicked)="$event.stopPropagation(); this.editNameIndex.set($index)"
                                ></s25-ng-icon-button>
                            }
                        </span>
                        @if (editNameIndex() === $index) {
                            <div
                                #nameForm
                                class="name-form"
                                role="dialog"
                                aria-label="Edit invoice name"
                                (click)="$event.stopPropagation()"
                            >
                                <label class="visually-hidden" for="invoice-name-input">Invoice name</label>
                                <input
                                    #nameInput
                                    id="invoice-name-input"
                                    class="c-input"
                                    [value]="displayName(invoice)"
                                    (input)="inputVal.set($any($event.target).value)"
                                    (keydown.escape)="editNameIndex.set(null)"
                                    maxlength="80"
                                />
                                @if (nameSaveError()) {
                                    <p class="ngRed" role="alert">{{ nameSaveError() }}</p>
                                }
                                <div class="aw-button-group">
                                    <button
                                        type="button"
                                        class="aw-button aw-button--outline"
                                        (click)="$event.stopPropagation(); editNameIndex.set(null)"
                                    >
                                        Cancel
                                    </button>
                                    <s25-ng-button
                                        [type]="'primary'"
                                        [onClick]="saveName"
                                        [disabled]="disableNameSave()"
                                        >Save</s25-ng-button
                                    >
                                </div>
                            </div>
                        }
                    </li>
                }
            </ul>
        </nav>
    `,
    host: {
        class: "sidebar-menu hide-on-mobile pricing-sidebar",
        "[class.closed]": "!sidebarOpen()",
    },
    styles: `
        /*
         * The class in the selector is only there for weight: a global \`.sidebar-menu\` rule sets a
         * 200px min-width, which would hold the sidebar open at full width.
         */
        :host(.pricing-sidebar) {
            min-width: 0;
            flex: 0 0 200px;
            transition: flex-basis 0.4s;

            &.closed {
                flex: 0 0 2.25em;
                overflow: hidden;
            }

            .action-bar {
                display: flex;
                justify-content: flex-end;
                margin: 5px;

                .c-textButton {
                    position: relative;
                    top: 1px;
                    color: var(--color--primary-fg) !important;
                    opacity: 1;
                    transition: opacity 1.5s ease-in;
                    margin-right: auto;

                    &.hidden {
                        opacity: 0;
                        transition: opacity 0.3s;
                    }
                }

                s25-ng-fading-success-msg {
                    margin-right: auto;
                }

                .menu-button {
                    background: none;
                    border: none;
                    padding: 0;
                    cursor: pointer;
                    color: var(--color--primary-fg);
                }
            }

            .visually-hidden {
                position: absolute;
                width: 1px;
                height: 1px;
                padding: 0;
                margin: -1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
                white-space: nowrap;
                border: 0;
            }

            nav {
                &.hidden {
                    opacity: 0;
                    transition: opacity 0.3s;
                }
            }

            ul {
                opacity: 1;
                transition: opacity 1.5s ease-in;
                list-style: none;
                margin: 0;
                padding-left: 0;
                padding-top: 0;

                s25-ng-icon-button {
                    display: none;
                    --icon-color: var(--cn-blue);
                    --color--primary-flat-bg: transparent;

                    &:hover {
                        --icon-color: var(--color--link-text-hover);
                    }
                }

                li {
                    position: relative;
                    margin: 1.75em 0;

                    &:hover,
                    &:focus,
                    &:focus-within {
                        s25-ng-icon-button {
                            display: unset;
                        }
                    }

                    &:focus {
                        outline: none;
                    }

                    .invoice-name:focus-visible {
                        outline: 2px solid currentColor;
                        outline-offset: -2px;
                    }

                    .name-form {
                        position: absolute;
                        background-color: var(--color--surface-bg);
                        z-index: 11;
                        padding: 10px;
                        left: 1.5em;
                        box-shadow: var(--shadow--dropdown);
                        border-radius: var(--border-radius);

                        .ngRed {
                            font-size: 0.9rem;
                        }

                        .aw-button-group {
                            display: flex;
                            justify-content: space-evenly;
                            padding-left: 0;
                        }
                    }

                    &:not(.active) {
                        &:hover,
                        &:focus,
                        &:focus-within {
                            .invoice-item {
                                color: var(--color-primary) !important;
                                background: var(--color--page-bg);
                            }
                        }
                    }

                    &.active {
                        &::before,
                        &::after {
                            content: none;
                        }

                        .invoice-item {
                            color: var(--color-primary) !important;
                            background: var(--color--page-bg);
                            border-radius: 10px 0 0 10px;

                            &::before,
                            &::after {
                                content: none;
                            }
                        }
                    }

                    .invoice-item {
                        display: flex;
                        align-items: flex-start;
                        padding: 0.5em 1em;
                        margin-left: 1.6em;
                        color: var(--color--primary-fg) !important;
                        position: relative;
                        border-bottom-left-radius: 10px;
                        border-top-left-radius: 10px;
                        transition:
                            background var(--transition-medium),
                            color var(--transition-medium);
                    }

                    .sortable {
                        padding-left: 5px;
                        gap: 5px;

                        .drag-handle {
                            font-size: 1rem;

                            &:hover {
                                cursor: move;
                            }
                        }

                        .drag-handle-spacer {
                            width: 1rem;
                            flex: none;
                        }

                        .invoice-name {
                            flex: 1 0 6.5em;
                        }

                        s25-ng-icon-button {
                            margin-left: auto;
                        }
                    }

                    .invoice-name {
                        flex: 1;
                        background: none;
                        border: none;
                        padding: 0;
                        font: inherit;
                        color: inherit;
                        text-align: left;
                        cursor: pointer;
                    }
                }
            }
        }

        :host ::ng-deep ~ div {
            margin-left: unset;
            padding-left: unset;
        }

        ::ng-deep .pricing-sidebar .menu-button svg:hover {
            cursor: pointer;
        }

        ::ng-deep .pricing-sidebar s25-ng-loading-inline-static circle {
            stroke: var(--color--primary-fg);
        }

        ::ng-deep .pricing-sidebar s25-ng-loading-inline-static .s25-text {
            display: none;
        }

        ::ng-deep .pricing-sidebar s25-ng-fading-success-msg .success-msg {
            color: var(--color--primary-fg);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25IconComponent,
        S25DndSortableModule,
        S25LoadingInlineModule,
        S25FadingSuccessMsgComponent,
        S25IconButtonComponent,
        S25ButtonComponent,
    ],
})
export class S25PricingSidebarComponent {
    private readonly pricingStore = inject(PricingStore);

    protected readonly displayName = invoiceDisplayName;

    readonly selectedInvoice = this.pricingStore.selectedInvoice;
    readonly combineRelated = this.pricingStore.combineRelated;
    readonly canEditPricing = this.pricingStore.canEditPricing;
    readonly eventId = this.pricingStore.eventId;
    readonly summaryActive = computed(() => this.pricingStore.sidebarView() === "summary");

    readonly canSort = computed(
        () =>
            this.canEditPricing() &&
            this.invoices().length > 1 &&
            this.editNameIndex() === null &&
            !this.combineRelated(),
    );
    readonly sortChanged = computed(
        () =>
            !S25Util.array.isEqual(
                this.invoices().map((invoice) => invoice.id),
                this.originalSortOrderById(),
            ),
    );

    // The reference set is left out: the summary reports on it, and it takes no part in the sort.
    // The default sets of related events are folded into the priced event's, so they show as one.
    readonly invoices = linkedSignal(() => this.pricingStore.displayedInvoices());

    readonly sidebarOpen = signal(true);
    readonly originalSortOrderById = linkedSignal(() => this.pricingStore.displayedInvoices().map((inv) => inv.id));
    readonly savingSort = signal(false);
    readonly successMsg = signal<string>("");
    readonly editNameIndex = signal<number | null>(null);

    readonly nameForm = viewChild<ElementRef>("nameForm");
    readonly nameInput = viewChild<ElementRef>("nameInput");

    readonly inputVal = linkedSignal(() => {
        const index = this.editNameIndex();
        if (index === null) return "";
        return invoiceDisplayName(this.invoices()[index]);
    });
    // Compared against the displayed name so that saving the default set untouched is a no-op
    // rather than writing the stand-in name to the server.
    readonly disableNameSave = computed(() => {
        const invoice = this.invoices()[this.editNameIndex() ?? -1];
        const name = this.inputVal();
        return !name.length || name === invoiceDisplayName(invoice);
    });
    readonly nameSaveError = signal("");

    constructor() {
        effect(() => {
            if (this.editNameIndex() !== null) {
                this.nameInput()?.nativeElement?.focus();
            }
        });
    }

    @HostListener("document:click", ["$event"])
    handleClick(event: MouseEvent) {
        const formEl = this.nameForm();
        if (formEl && !formEl.nativeElement.contains(event.target)) this.editNameIndex.set(null);
    }

    pivotSidebar() {
        this.sidebarOpen.update((prev) => !prev);
    }

    showSummary() {
        this.pricingStore.setSidebarView("summary");
    }

    selectInvoice(invoice: Invoice) {
        this.pricingStore.setEvBillId(invoice.id);
        this.pricingStore.setSidebarView("invoices");
    }

    onOrderChanged(invoices: Invoice[]) {
        this.invoices.set([...invoices]);
    }

    async saveSort() {
        const eventId = this.eventId();
        if (!eventId) return;

        this.savingSort.set(true);
        const payload = this.invoices().map((inv, index) => ({
            evBillId: inv.id,
            sortOrder: index + 1,
            created: inv.billDefn!.created,
        }));
        const [, error] = await S25Util.Maybe(PricingService.putInvoiceSortOrder(eventId, payload));
        this.savingSort.set(false);
        if (error) return S25Util.showError(error);
        this.successMsg.set("Saved!");
        // The store keeps every invoice, so the ones the sidebar never listed - the reference sets,
        // and the default sets folded into the priced event's - have to survive a re-sort.
        this.pricingStore.applySortOrder(this.invoices().map((invoice) => invoice.id));
    }

    @Bind
    async saveName() {
        const index = this.editNameIndex();
        if (index === null) return;
        const invoice = this.invoices()[index];
        const name = this.inputVal();
        if (invoice?.id && name) {
            const [, error] = await S25Util.Maybe(PricingService.putBillingCustom({ billName: name }, invoice.id));
            if (error) {
                this.nameSaveError.set(S25Util.errorText(error, ""));
                return console.error(error);
            }
            this.pricingStore.updateInvoiceName(invoice.id, name);
            this.editNameIndex.set(null);
            this.nameSaveError.set("");
        }
    }
}
